import { type TTweenEasing, tweenEasings } from './easings'

export function createTicker(handler: (delta: number) => void) {
  let isActive = true
  let frameHandle: number
  let previousTime: number = 0
  function frame(newTime: number) {
    if (!isActive) return
    if (previousTime !== 0) {
      const delta = (newTime - previousTime) / 1000
      handler(delta)
    }
    previousTime = newTime
    frameHandle = requestAnimationFrame(frame)
  }
  frame(0)
  return () => {
    cancelAnimationFrame(frameHandle)
    isActive = false
  }
}

interface TTweenOptions<T extends object> {
  duration?: number
  delay?: number
  onStart?: (object: T) => void
  onUpdate?: (object: T) => void
  onComplete?: (object: T) => void
  ease?: TTweenEasing
}

export interface TTweenInstance<T extends object> {
  target: T
  options: TTweenOptions<T>
  stop: (goToEnd?: boolean) => void
  cancelProperty: (key: keyof T) => void
}

export function tween<T extends { _tweens?: TTweenInstance<T>[] } & object>(
  target: T,
  properties: Partial<T>,
  options: TTweenOptions<T> = {}
) {
  // Init tweens array in object to track running tweens on a target
  target._tweens ??= []
  // Extract options
  const duration = options.duration ?? 1.0
  const delay = options.delay ?? 0.0
  const maxTime = duration + delay
  const ease: TTweenEasing = options.ease ?? tweenEasings.linear
  const { onStart, onUpdate, onComplete } = options
  // Extract properties
  // oxlint-disable-next-line typescript/consistent-type-assertions
  const keys = Object.keys(properties) as (keyof T)[]
  // Extract start values
  const startValues = keys.map(k => target[k])
  // Init timer
  let ticker: (() => void) | null
  let currentTime = 0
  // Started is false before delay
  let started = false
  // Declared early so the kill()/overrideOtherTweens() closures can reference it;
  // assigned exactly once below.
  // eslint-disable-next-line prefer-const
  let tweenObject: TTweenInstance<T>

  // Stop running tweens on this target
  // We only remove same properties and keep others running
  function overrideOtherTweens() {
    if (Array.isArray(target._tweens) && target._tweens.length > 0) {
      target._tweens.forEach(t => {
        if (t === tweenObject) return
        keys.forEach(k => {
          t.cancelProperty(k)
        })
      })
    }
  }

  // Stop tween
  function kill() {
    if (!ticker) return
    ticker()
    ticker = null
    target._tweens = target._tweens?.filter(t => t !== tweenObject)
  }

  // Set target to destination
  function setToDestination() {
    keys.forEach(k => {
      // @ts-expect-error - dynamic property assignment
      target[k] = properties[k]
    })
    onUpdate?.(target)
    onComplete?.(target)
  }

  // Stop tween
  function stop(goToEnd: boolean = false) {
    if (!ticker) return
    kill()
    if (goToEnd) {
      setToDestination()
    }
  }

  // Update frame
  function update(delta: number) {
    if (!ticker) {
      return
    }
    // Increment time from ticker with delta
    currentTime = Math.min(maxTime, currentTime + delta)
    // Finished
    if (currentTime < delay) return
    if (!started) {
      started = true
      onStart?.(target)
    }
    if (currentTime >= maxTime) {
      stop(true)
      return
    }
    // Compute position from 0 to 1
    const position = (currentTime - delay) / duration
    // Update all positions
    keys.forEach((k, i) => {
      const output = ease(position)
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const start = startValues[i] as number
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const end = properties[k] as number
      // @ts-expect-error - dynamic property assignment
      target[k] = start + (end - start) * output
    })
    onUpdate?.(target)
  }

  function cancelProperty(key: keyof T) {
    const index = keys.indexOf(key)
    if (index === -1) return
    keys.splice(index, 1)
    startValues.splice(index, 1)
    if (keys.length === 0) {
      kill()
    }
  }

  // Create tween object
  tweenObject = {
    target,
    options,
    stop,
    cancelProperty
  }
  overrideOtherTweens()
  if (duration === 0 && delay === 0) {
    // Direct setter
    setToDestination()
  } else {
    // Start animation
    ticker = createTicker(update)
    target._tweens.push(tweenObject)
  }
  return tweenObject
}
